import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowUp, ArrowDown, Plus } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

function Check({ checked, disabled, onChange, testId, label }) {
  return (
    <label className={`flex items-center gap-1.5 text-xs ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} className="w-4 h-4 accent-brand-green" data-testid={testId} />{label}
    </label>
  );
}

export default function PaymentMethodsTab() {
  const [methods, setMethods] = useState(null);
  const [newLabel, setNewLabel] = useState('');
  const load = useCallback(() => axios.get(`${API}/api/payment-methods`, { params: { all: true } }).then(r => setMethods(r.data)).catch(() => toast.error('Erro ao carregar métodos')), []);
  useEffect(() => { load(); }, [load]);
  const save = async (m, patch) => {
    try { await axios.put(`${API}/api/payment-methods/${m.id}`, patch); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao guardar'); }
  };
  const add = async () => {
    if (!newLabel.trim()) return;
    try { await axios.post(`${API}/api/payment-methods`, { label: newLabel.trim() }); setNewLabel(''); load(); toast.success('Método criado'); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const move = async (i, d) => {
    const ids = methods.map(m => m.id); const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await axios.put(`${API}/api/payment-methods/reorder/all`, { ids }); load();
  };
  return (
    <div className="space-y-4" data-testid="payment-methods-tab">
      <div><h2 className="font-heading font-medium text-xl text-brand-text">Métodos de pagamento</h2>
        <p className="text-xs text-brand-muted">Usados no formulário da consulta. Os métodos de parceria são criados sozinhos a partir das categorias marcadas como Parceria. Os métodos nunca se apagam: desativam-se.</p></div>
      {!methods ? <p className="text-xs text-brand-muted">A carregar...</p> : (
        <div className="space-y-2">
          {methods.map((m, i) => {
            const locked = m.kind === 'partnership' || ['pacote', 'cheque_nutricionista'].includes(m.key);
            return (
              <div key={m.id} className={`bg-white border rounded-2xl p-3 flex flex-wrap items-center gap-3 ${m.active ? 'border-brand-border' : 'border-dashed border-brand-border opacity-60'}`} data-testid={`pm-row-${m.key}`}>
                <div className="flex flex-col"><button onClick={() => move(i, -1)} className="text-brand-muted" data-testid={`pm-up-${m.key}`}><ArrowUp size={12} /></button><button onClick={() => move(i, 1)} className="text-brand-muted" data-testid={`pm-down-${m.key}`}><ArrowDown size={12} /></button></div>
                <Input defaultValue={m.label} disabled={m.kind === 'partnership'} onBlur={e => { if (e.target.value.trim() && e.target.value !== m.label) save(m, { label: e.target.value }); }} className="w-56 rounded-lg border-brand-border" data-testid={`pm-label-${m.key}`} />
                {m.kind === 'partnership' && <span className="text-[10px] bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">Parceria</span>}
                <Check checked={!!m.show_payment_button} disabled={locked} onChange={v => save(m, { show_payment_button: v })} testId={`pm-button-${m.key}`} label="Mostrar botão de pagamento nos emails" />
                <Check checked={!!m.counts_as_direct} disabled={locked} onChange={v => save(m, { counts_as_direct: v })} testId={`pm-direct-${m.key}`} label="Conta como recebido diretamente da paciente" />
                <Check checked={!!m.active} onChange={v => save(m, { active: v })} testId={`pm-active-${m.key}`} label="Ativo" />
                {m.show_payment_button && m.key !== 'stripe' && (
                  <div className="w-full" data-testid={`pm-instructions-wrap-${m.key}`}>
                    <label className="text-[11px] text-brand-muted block mb-1">Instruções para a paciente {m.key === 'numerario' && <span>(só aparece em consultas presenciais)</span>}</label>
                    <Textarea key={`${m.id}-${m.instructions || ''}`} defaultValue={m.instructions || ''} rows={3} maxLength={2000}
                      placeholder={m.key === 'transferencia' ? 'Ex.: IBAN PT50 ... · Titular: ...' : m.key === 'mb_way' ? 'Ex.: MB Way para o número 9XX XXX XXX' : m.key === 'numerario' ? 'Ex.: Pagamento no consultório, no dia da consulta' : 'Instruções de pagamento'}
                      onBlur={e => { if ((e.target.value || '').trim() !== (m.instructions || '').trim()) save(m, { instructions: e.target.value }); }}
                      className="rounded-xl border-brand-border text-sm" data-testid={`pm-instructions-${m.key}`} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="flex gap-2">
        <Input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="Novo método (ex.: Cartão na clínica)" className="w-72 rounded-lg border-brand-border" data-testid="pm-new-label" />
        <button onClick={add} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium flex items-center gap-1" data-testid="pm-add-btn"><Plus size={12} /> Adicionar</button>
      </div>
    </div>
  );
}
